/**
 * PivotSidebar — Shared sidebar with switchable grouping dimension
 *
 * Groups any list of items by a user-selected "pivot" dimension (e.g., Campaign,
 * Content Type, Status, Doc Type). Used by both Product Documentation and GTM Library.
 *
 * Uses ReaderSidebarShell for visual chrome.
 */

import { useState, useMemo, useCallback, type ReactNode } from 'react';
import { Search, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { ReaderSidebarShell } from './ReaderSidebarShell';
import { DocTreeItem, type DocTreeNode } from '../documentation/DocTreeItem';
import type { EditorDocument } from '../../api/documents';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PivotDimension<T> {
  id: string;
  label: string;
  getKey: (item: T) => string | null;
  getLabel?: (key: string) => string;
  getIcon?: (key: string) => ReactNode;
}

export interface PivotFilterOption {
  value: string;
  label: string;
}

export interface PivotSidebarProps<T> {
  title: string;
  items: T[];
  dimensions: PivotDimension<T>[];
  defaultDimension?: string;
  activeItemId?: string;
  isLoading?: boolean;
  headerActions: ReactNode;
  getItemId: (item: T) => string;
  getItemTitle: (item: T) => string;
  getItemIcon?: (item: T) => ReactNode;
  getItemUpdatedAt: (item: T) => string;
  getItemSearchText?: (item: T) => string;
  onItemSelect: (item: T) => void;
  filterOptions?: PivotFilterOption[];
  filterFn?: (item: T, filterValue: string) => boolean;
  storageKey?: string;
  /** Max items shown per section before "show more" (default: 8) */
  sectionLimit?: number;
  /**
   * When provided, enables the "Hierarchy" pivot dimension which renders a
   * collapsible parent/child tree instead of flat grouped sections.
   * Return null/undefined for root items; return the parent's ID for children.
   */
  getParentId?: (item: T) => string | null | undefined;
}

// ── Sort presets ─────────────────────────────────────────────────────────────

type SortPreset = 'updated-desc' | 'updated-asc' | 'title-asc' | 'title-desc';

const SORT_PRESETS: { value: SortPreset; label: string }[] = [
  { value: 'updated-desc', label: 'Newest' },
  { value: 'updated-asc', label: 'Oldest' },
  { value: 'title-asc', label: 'Title A–Z' },
  { value: 'title-desc', label: 'Title Z–A' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function readStorage<V>(key: string, fallback: V): V {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore */ }
}

// ── Hierarchy tree builder ────────────────────────────────────────────────────

/**
 * Builds a DocTreeNode[] from a flat item list using a getParentId accessor.
 * Items whose parent is not in the list (or have no parent) become roots.
 * Safe to cast T→EditorDocument because this is only called when getParentId is
 * provided, which only happens in DocumentationReader where T=EditorDocument.
 */
function buildHierarchyTree<T>(
  items: T[],
  getId: (item: T) => string,
  getParentId: (item: T) => string | null | undefined,
): DocTreeNode[] {
  const ids = new Set(items.map(getId));
  const childrenOf = new Map<string, T[]>();
  const roots: T[] = [];

  for (const item of items) {
    const pid = getParentId(item);
    const parentKey = pid && ids.has(pid) ? pid : null;
    if (parentKey === null) {
      roots.push(item);
    } else {
      if (!childrenOf.has(parentKey)) childrenOf.set(parentKey, []);
      childrenOf.get(parentKey)!.push(item);
    }
  }

  function toNodes(list: T[]): DocTreeNode[] {
    return list.map((item) => ({
      document: item as unknown as EditorDocument,
      children: toNodes(childrenOf.get(getId(item)) || []),
    }));
  }

  return toNodes(roots);
}

// ── Component ────────────────────────────────────────────────────────────────

export function PivotSidebar<T>({
  title,
  items,
  dimensions,
  defaultDimension,
  activeItemId,
  isLoading = false,
  headerActions,
  getItemId,
  getItemTitle,
  getItemIcon,
  getItemUpdatedAt,
  getItemSearchText,
  onItemSelect,
  filterOptions,
  filterFn,
  storageKey = 'pivot-sidebar',
  sectionLimit = 8,
  getParentId,
}: PivotSidebarProps<T>) {
  // ── Persisted state ─────────────────────────────────────────────────────
  const [pivotId, setPivotId] = useState<string>(
    () => readStorage(`${storageKey}-pivot`, defaultDimension || dimensions[0]?.id || '')
  );
  const [expandedSet, setExpandedSet] = useState<Set<string>>(
    () => new Set(readStorage<string[]>(`${storageKey}-expanded`, []))
  );
  const [expandedFull, setExpandedFull] = useState<Set<string>>(new Set());

  // ── Local state ─────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [sortPreset, setSortPreset] = useState<SortPreset>('updated-desc');
  const [filterValue, setFilterValue] = useState('');

  // ── Resolve active dimension ────────────────────────────────────────────
  const dimension = dimensions.find((d) => d.id === pivotId) || dimensions[0];

  const handlePivotChange = useCallback(
    (id: string) => {
      setPivotId(id);
      writeStorage(`${storageKey}-pivot`, id);
      setExpandedFull(new Set());
      // Auto-expand all sections on pivot switch
      setExpandedSet(new Set());
    },
    [storageKey]
  );

  const toggleSection = useCallback(
    (key: string) => {
      setExpandedSet((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        writeStorage(`${storageKey}-expanded`, Array.from(next));
        return next;
      });
    },
    [storageKey]
  );

  const toggleShowAll = useCallback((key: string) => {
    setExpandedFull((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ── Filter + sort pipeline ──────────────────────────────────────────────

  const processed = useMemo(() => {
    let result = items;

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((item) => {
        const searchText = getItemSearchText
          ? getItemSearchText(item)
          : getItemTitle(item);
        return searchText.toLowerCase().includes(q);
      });
    }

    // Extra filter (e.g., content type)
    if (filterValue && filterFn) {
      result = result.filter((item) => filterFn(item, filterValue));
    }

    // Sort
    const [field, dir] = sortPreset.split('-') as ['updated' | 'title', 'asc' | 'desc'];
    result = [...result].sort((a, b) => {
      let cmp = 0;
      if (field === 'title') {
        cmp = getItemTitle(a).localeCompare(getItemTitle(b));
      } else {
        cmp = new Date(getItemUpdatedAt(a)).getTime() - new Date(getItemUpdatedAt(b)).getTime();
      }
      return dir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [items, searchQuery, filterValue, filterFn, sortPreset, getItemTitle, getItemUpdatedAt, getItemSearchText]);

  // ── Group by pivot dimension ────────────────────────────────────────────

  const sections = useMemo(() => {
    const groups = new Map<string, T[]>();
    const uncategorized: T[] = [];

    for (const item of processed) {
      const key = dimension.getKey(item);
      if (!key) {
        uncategorized.push(item);
      } else {
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(item);
      }
    }

    // Sort section keys alphabetically
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
      const labelA = dimension.getLabel ? dimension.getLabel(a) : a;
      const labelB = dimension.getLabel ? dimension.getLabel(b) : b;
      return labelA.localeCompare(labelB);
    });

    const result: { key: string; label: string; icon?: ReactNode; items: T[] }[] = sortedKeys.map(
      (key) => ({
        key,
        label: dimension.getLabel ? dimension.getLabel(key) : key,
        icon: dimension.getIcon ? dimension.getIcon(key) : undefined,
        items: groups.get(key)!,
      })
    );

    if (uncategorized.length > 0) {
      result.push({ key: '__uncategorized__', label: 'Uncategorized', items: uncategorized });
    }

    return result;
  }, [processed, dimension]);

  // ── Auto-expand first section on first load ─────────────────────────────
  const isFirstLoad = expandedSet.size === 0 && sections.length > 0;
  if (isFirstLoad && sections[0]) {
    expandedSet.add(sections[0].key);
  }

  // ── Hierarchy tree (only when pivotId === 'hierarchy' and getParentId is set) ──
  const hierarchyTree = useMemo(() => {
    if (pivotId !== 'hierarchy' || !getParentId) return null;
    return buildHierarchyTree(processed, getItemId, getParentId);
  }, [pivotId, getParentId, processed, getItemId]);

  // ── Footer ──────────────────────────────────────────────────────────────

  const isFiltered = searchQuery.trim() || filterValue;
  const footerText = isFiltered
    ? `${processed.length} result${processed.length !== 1 ? 's' : ''}`
    : `${items.length} item${items.length !== 1 ? 's' : ''}`;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <ReaderSidebarShell
      title={title}
      headerActions={headerActions}
      footer={footerText}
      isLoading={isLoading}
    >
      {/* Search */}
      <div className="px-3 py-2 shrink-0">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}...`}
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-surface-muted border-0 rounded-md
              placeholder:text-subtle focus:ring-1 focus:ring-coral-200
              focus:bg-white transition-all"
          />
        </div>
      </div>

      {/* Controls row */}
      <div className="px-3 pb-2 shrink-0 flex gap-1.5 items-center">
        {/* Pivot selector */}
        <select
          value={pivotId}
          onChange={(e) => handlePivotChange(e.target.value)}
          className="flex-1 min-w-0 text-xs bg-surface-muted border-0 rounded-md py-1.5 px-2
            focus:ring-1 focus:ring-coral-200 focus:bg-white transition-all text-muted"
        >
          {dimensions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.label}
            </option>
          ))}
        </select>

        {/* Extra filter (e.g., content type) */}
        {filterOptions && (
          <select
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            className="flex-1 min-w-0 text-xs bg-surface-muted border-0 rounded-md py-1.5 px-2
              focus:ring-1 focus:ring-coral-200 focus:bg-white transition-all text-muted"
          >
            <option value="">All</option>
            {filterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        {/* Sort */}
        <select
          value={sortPreset}
          onChange={(e) => setSortPreset(e.target.value as SortPreset)}
          className="flex-1 min-w-0 text-xs bg-surface-muted border-0 rounded-md py-1.5 px-2
            focus:ring-1 focus:ring-coral-200 focus:bg-white transition-all text-muted"
        >
          {SORT_PRESETS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Hierarchy tree (when "Hierarchy" pivot is active) */}
      {hierarchyTree && (
        <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5">
          {hierarchyTree.map((node) => (
            <DocTreeItem
              key={node.document.id}
              node={node}
              activeDocId={activeItemId}
              onDocumentSelect={(doc) => {
                const item = items.find((i) => getItemId(i) === doc.id);
                if (item) onItemSelect(item);
              }}
            />
          ))}
          {searchQuery.trim() && hierarchyTree.length === 0 && (
            <p className="px-3 py-6 text-xs text-subtle text-center">
              No items match &ldquo;{searchQuery}&rdquo;
            </p>
          )}
        </nav>
      )}

      {/* Sections (flat grouped view — used for all non-hierarchy pivots) */}
      {!hierarchyTree && (
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
        {sections.map((section, index) => {
          const isExpanded = expandedSet.has(section.key);
          const showAll = expandedFull.has(section.key);
          const visibleItems = showAll ? section.items : section.items.slice(0, sectionLimit);
          const hiddenCount = section.items.length - sectionLimit;

          return (
            <div
              key={section.key}
              className={index > 0 ? 'border-t border-line pt-3 mt-2' : ''}
            >
              <button
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center justify-between px-2 py-1.5 text-[11px]
                  font-medium text-subtle hover:text-muted transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  {section.icon}
                  <span>{section.label}</span>
                  <span className="text-subtle">&middot;</span>
                  <span className="text-subtle">{section.items.length}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown size={12} className="text-subtle" />
                ) : (
                  <ChevronRight size={12} className="text-subtle" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-1 space-y-0.5">
                  {visibleItems.map((item) => {
                    const id = getItemId(item);
                    const isActive = id === activeItemId;
                    return (
                      <button
                        key={id}
                        onClick={() => onItemSelect(item)}
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-colors group
                          ${isActive
                            ? 'bg-coral-50 text-coral-700'
                            : 'text-text hover:bg-surface-muted'
                          }`}
                      >
                        {getItemIcon ? (
                          <span className={`flex-shrink-0 ${isActive ? 'text-coral-500' : 'text-subtle'}`}>
                            {getItemIcon(item)}
                          </span>
                        ) : (
                          <FileText
                            size={14}
                            className={`flex-shrink-0 ${isActive ? 'text-coral-500' : 'text-subtle'}`}
                          />
                        )}
                        <span className={`flex-1 min-w-0 text-xs truncate ${isActive ? 'font-medium' : 'font-normal'}`}>
                          {getItemTitle(item)}
                        </span>
                        <ChevronRight
                          size={12}
                          className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity
                            ${isActive ? 'text-coral-400' : 'text-subtle'}`}
                        />
                      </button>
                    );
                  })}

                  {/* Show more button */}
                  {!showAll && hiddenCount > 0 && (
                    <button
                      onClick={() => toggleShowAll(section.key)}
                      className="w-full px-2.5 py-1.5 text-[11px] text-coral-500 hover:text-coral-600
                        hover:bg-coral-50/50 rounded-lg transition-colors text-left"
                    >
                      + {hiddenCount} more
                    </button>
                  )}
                  {showAll && hiddenCount > 0 && (
                    <button
                      onClick={() => toggleShowAll(section.key)}
                      className="w-full px-2.5 py-1.5 text-[11px] text-subtle hover:text-muted
                        hover:bg-surface-muted rounded-lg transition-colors text-left"
                    >
                      Show less
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {searchQuery.trim() && sections.length === 0 && (
          <p className="px-3 py-6 text-xs text-subtle text-center">
            No items match &ldquo;{searchQuery}&rdquo;
          </p>
        )}
      </nav>
      )}
    </ReaderSidebarShell>
  );
}

export default PivotSidebar;
